// Generated from Ferngate LLC's Termly privacy policy export.
// Keep this file in sync with future Termly re-exports rather than hand-editing prose.
export default function PrivacyPolicyContent() {
  return (
    <>
      <div className="legal-page__glance">
        <p className="legal-page__glance-title">Privacy at a glance</p>
        <p>
          Ferngate is a task management app, and we keep data collection to the minimum needed to
          run it.
        </p>
        <ul>
          <li>
            <strong>What we collect:</strong> your name, email, password, and the tasks you
            create.
          </li>
          <li>
            <strong>How AI is used:</strong> to create your productivity reports, we send only
            task titles, priorities, and due dates to Anthropic (the maker of Claude). Your notes
            and account details are never sent.
          </li>
          <li>
            <strong>What we don't do:</strong> we don't sell your data, show ads, or use tracking
            cookies.
          </li>
          <li>
            <strong>Questions or requests:</strong> contact us at{' '}
            <a href="mailto:support@ferngate.net">support@ferngate.net</a>.
          </li>
        </ul>
        <p className="legal-page__glance-footer">The full policy below has the details.</p>
      </div>

      <p>
        This Privacy Notice for Ferngate LLC ("we," "us," or "our"), describes how and why we
        might access, collect, store, use, and/or share ("process") your personal information
        when you use our services ("Services"), including when you:
      </p>
      <ul>
        <li>
          Visit our website at{' '}
          <a href="https://ferngate.net/" target="_blank" rel="noopener noreferrer">
            https://ferngate.net/
          </a>{' '}
          or any website of ours that links to this Privacy Notice
        </li>
        <li>
          Use Ferngate, our task management app that provides AI-generated productivity reports
        </li>
      </ul>
      <p>
        <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you
        understand your privacy rights and choices. If you have any questions or concerns, please
        contact us at <a href="mailto:support@ferngate.net">support@ferngate.net</a>.
      </p>

      <h2>SUMMARY OF KEY POINTS</h2>
      <p>
        <em>
          This summary provides key points from our Privacy Notice, but you can find out more
          details about any of these topics by clicking the link following each key point or by
          using our <a href="#toc">table of contents</a> below to find the section you are
          looking for.
        </em>
      </p>
      <p>
        <strong>What personal information do we process?</strong> When you visit, use, or
        navigate our Services, we may process personal information depending on how you interact
        with us and the Services, the choices you make, and the products and features you use.
        Learn more about <a href="#personalinfo">personal information you disclose to us</a>.
      </p>
      <p>
        <strong>Do we process any sensitive personal information?</strong> The only information we
        process that some laws consider sensitive is your account login credentials (email address
        and password), which we use solely to let you sign in. Learn more about{' '}
        <a href="#sensitiveinfo">sensitive information we process</a>.
      </p>
      <p>
        <strong>Do we collect any information from third parties?</strong> We do not collect any
        information from third parties.
      </p>
      <p>
        <strong>How do we process your information?</strong> We process your information to
        provide, improve, and administer our Services, communicate with you, for security and
        fraud prevention, and to comply with law. We may also process your information for other
        purposes with your consent. We process your information only when we have a valid legal
        reason to do so. Learn more about{' '}
        <a href="#infouse">how we process your information</a>.
      </p>
      <p>
        <strong>In what situations and with which parties do we share personal information?</strong>{' '}
        We may share information in specific situations and with specific third parties. Learn
        more about{' '}
        <a href="#whoshare">when and with whom we share your personal information</a>.
      </p>
      <p>
        <strong>How do we keep your information safe?</strong> We have adequate organizational and
        technical processes and procedures in place to protect your personal information. However,
        no electronic transmission over the internet or information storage technology can be
        guaranteed to be 100% secure, so we cannot promise or guarantee that hackers,
        cybercriminals, or other unauthorized third parties will not be able to defeat our
        security and improperly collect, access, steal, or modify your information. Learn more
        about <a href="#infosafe">how we keep your information safe</a>.
      </p>
      <p>
        <strong>What are your rights?</strong> Depending on where you are located geographically,
        the applicable privacy law may mean you have certain rights regarding your personal
        information. Learn more about <a href="#privacyrights">your privacy rights</a>.
      </p>
      <p>
        <strong>How do you exercise your rights?</strong> The easiest way to exercise your rights
        is by contacting us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a>. We will consider and act
        upon any request in accordance with applicable data protection laws.
      </p>
      <p>
        Want to learn more about what we do with any information we collect?{' '}
        <a href="#toc">Review the Privacy Notice in full</a>.
      </p>

      <h2 id="toc">TABLE OF CONTENTS</h2>
      <ul>
        <li><a href="#infocollect">1. WHAT INFORMATION DO WE COLLECT?</a></li>
        <li><a href="#infouse">2. HOW DO WE PROCESS YOUR INFORMATION?</a></li>
        <li>
          <a href="#legalbases">
            3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
          </a>
        </li>
        <li>
          <a href="#whoshare">4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a>
        </li>
        <li><a href="#ai">5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</a></li>
        <li><a href="#intltransfers">6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</a></li>
        <li><a href="#inforetain">7. HOW LONG DO WE KEEP YOUR INFORMATION?</a></li>
        <li><a href="#infosafe">8. HOW DO WE KEEP YOUR INFORMATION SAFE?</a></li>
        <li><a href="#infominors">9. DO WE COLLECT INFORMATION FROM MINORS?</a></li>
        <li><a href="#privacyrights">10. WHAT ARE YOUR PRIVACY RIGHTS?</a></li>
        <li><a href="#DNT">11. CONTROLS FOR DO-NOT-TRACK FEATURES</a></li>
        <li>
          <a href="#uslaws">12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</a>
        </li>
        <li><a href="#clausea">13. A NOTE ON WHAT YOU ENTER</a></li>
        <li><a href="#policyupdates">14. DO WE MAKE UPDATES TO THIS NOTICE?</a></li>
        <li><a href="#contact">15. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a></li>
        <li>
          <a href="#request">
            16. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </a>
        </li>
      </ul>

      <h2 id="infocollect">1. WHAT INFORMATION DO WE COLLECT?</h2>
      <h3 id="personalinfo">Personal information you disclose to us</h3>
      <p>
        <em>In Short: We collect personal information that you provide to us.</em>
      </p>
      <p>
        We collect personal information that you voluntarily provide to us when you register on
        the Services, express an interest in obtaining information about us or our products and
        Services, when you participate in activities on the Services, or otherwise when you
        contact us.
      </p>
      <p>
        <strong>Personal Information Provided by You.</strong> The personal information that we
        collect depends on the context of your interactions with us and the Services, the choices
        you make, and the products and features you use. The personal information we collect may
        include the following:
      </p>
      <ul>
        <li>names</li>
        <li>email addresses</li>
        <li>passwords</li>
      </ul>
      <p id="sensitiveinfo">
        <strong>Sensitive Information.</strong> When necessary, with your consent or as otherwise
        permitted by applicable law, we process the following categories of sensitive
        information:
      </p>
      <ul>
        <li>account login credentials (email address and password)</li>
      </ul>
      <p>
        All personal information that you provide to us must be true, complete, and accurate, and
        you must notify us of any changes to such personal information.
      </p>

      <h3>Information automatically collected</h3>
      <p>
        <em>
          In Short: Some information — such as your Internet Protocol (IP) address and/or browser
          and device characteristics — is collected automatically when you visit our Services.
        </em>
      </p>
      <p>
        We automatically collect certain technical information when you visit, use, or navigate
        the Services, such as your IP address, browser and device characteristics, operating
        system, language preferences, referring URLs, and information about how and when you use
        our Services. When you're signed in, this information may be associated with your
        account. We use it primarily to maintain the security and operation of our Services, and
        for our internal analytics and reporting purposes.
      </p>
      <p>The information we collect includes:</p>
      <ul>
        <li>
          <em>Log and Usage Data.</em> Log and usage data is service-related, diagnostic, usage,
          and performance information our servers automatically collect when you access or use our
          Services and which we record in log files. Depending on how you interact with us, this
          log data may include your IP address, device information, browser type, and settings and
          information about your activity in the Services (such as the date/time stamps associated
          with your usage, pages and files viewed, searches, and other actions you take such as
          which features you use), device event information (such as system activity, error
          reports (sometimes called "crash dumps"), and hardware settings).
        </li>
      </ul>

      <h2 id="infouse">2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
      <p>
        <em>
          In Short: We process your information to provide, improve, and administer our Services,
          communicate with you, for security and fraud prevention, and to comply with law. We
          process the personal information for the following purposes listed below. We may also
          process your information for other purposes only with your prior explicit consent.
        </em>
      </p>
      <p>
        <strong>
          We process your personal information for a variety of reasons, depending on how you
          interact with our Services, including:
        </strong>
      </p>
      <ul>
        <li>
          <strong>
            To facilitate account creation and authentication and otherwise manage user accounts.
          </strong>{' '}
          We may process your information so you can create and log in to your account, as well
          as keep your account in working order.
        </li>
        <li>
          <strong>To deliver and facilitate delivery of services to the user.</strong> We may
          process your information to provide you with the requested service.
        </li>
        <li>
          <strong>To respond to user inquiries/offer support to users.</strong> We may process
          your information to respond to your inquiries and solve any potential issues you might
          have with the requested service.
        </li>
        <li>
          <strong>To send administrative information to you.</strong> We may process your
          information to send you details about our products and services, changes to our terms
          and policies, and other similar information.
        </li>
        <li>
          <strong>To request feedback.</strong> We may process your information when necessary to
          request feedback and to contact you about your use of our Services.
        </li>
        <li>
          <strong>To protect our Services.</strong> We may process your information as part of our
          efforts to keep our Services safe and secure, including fraud monitoring and prevention.
        </li>
        <li>
          <strong>To identify usage trends.</strong> We may process information about how you use
          our Services to better understand how they are being used so we can improve them.
        </li>
        <li>
          <strong>To save or protect an individual's vital interest.</strong> We may process your
          information when necessary to save or protect an individual's vital interest, such as to
          prevent harm.
        </li>
        <li>
          <strong>To generate AI productivity reports.</strong> We send your task titles,
          priorities, and due dates (for pending or overdue tasks) to our AI provider, Anthropic,
          to create your weekly productivity report. Task notes and account details are never
          sent. Anthropic processes this data under its commercial terms and privacy policy.
        </li>
      </ul>

      <h2 id="legalbases">3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?</h2>
      <p>
        <em>
          In Short: We only process your personal information when we believe it is necessary and
          we have a valid legal reason (i.e., legal basis) to do so under applicable law, like
          with your consent, to comply with laws, to provide you with services to enter into or
          fulfill our contractual obligations, to protect your rights, or to fulfill our
          legitimate business interests.
        </em>
      </p>
      <p>
        <strong>If you are located in the EU or UK, this section applies to you.</strong>
      </p>
      <p>
        The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid
        legal bases we rely on in order to process your personal information. As such, we may rely
        on the following legal bases to process your personal information:
      </p>
      <ul>
        <li>
          <strong>Consent.</strong> We may process your information if you have given us
          permission (i.e., consent) to use your personal information for a specific purpose. You
          can withdraw your consent at any time. Learn more about{' '}
          <a href="#withdrawconsent">withdrawing your consent</a>.
        </li>
        <li>
          <strong>Performance of a Contract.</strong> We may process your personal information
          when we believe it is necessary to fulfill our contractual obligations to you, including
          providing our Services or at your request prior to entering into a contract with you.
        </li>
        <li>
          <strong>Legitimate Interests.</strong> We may process your information when we believe
          it is reasonably necessary to achieve our legitimate business interests and those
          interests do not outweigh your interests and fundamental rights and freedoms. For
          example, we may process your personal information for some of the purposes described in
          order to:
          <ul>
            <li>Analyze how our Services are used so we can improve them to engage and retain users</li>
            <li>Diagnose problems and/or prevent fraudulent activities</li>
            <li>
              Understand how our users use our products and services so we can improve user
              experience
            </li>
          </ul>
        </li>
        <li>
          <strong>Legal Obligations.</strong> We may process your information where we believe it
          is necessary for compliance with our legal obligations, such as to cooperate with a law
          enforcement body or regulatory agency, exercise or defend our legal rights, or disclose
          your information as evidence in litigation in which we are involved.
        </li>
        <li>
          <strong>Vital Interests.</strong> We may process your information where we believe it is
          necessary to protect your vital interests or the vital interests of a third party, such
          as situations involving potential threats to the safety of any person.
        </li>
      </ul>
      <p>
        <strong>If you are located in Canada, this section applies to you.</strong>
      </p>
      <p>
        We may process your information if you have given us specific permission (i.e., express
        consent) to use your personal information for a specific purpose, or in situations where
        your permission can be inferred (i.e., implied consent). You can{' '}
        <a href="#withdrawconsent">withdraw your consent</a> at any time by contacting us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a>.
      </p>
      <p>
        In some exceptional cases, we may be legally permitted under applicable law to process
        your information without your consent, including, for example:
      </p>
      <ul>
        <li>If collection is clearly in the interests of an individual and consent cannot be obtained in a timely way</li>
        <li>For investigations and fraud detection and prevention</li>
        <li>For business transactions provided certain conditions are met</li>
        <li>If it is contained in a witness statement and the collection is necessary to assess, process, or settle an insurance claim</li>
        <li>For identifying injured, ill, or deceased persons and communicating with next of kin</li>
        <li>If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse</li>
        <li>
          If it is reasonable to expect collection and use with consent would compromise the
          availability or the accuracy of the information and the collection is reasonable for
          purposes related to investigating a breach of an agreement or a contravention of the laws
          of Canada or a province
        </li>
        <li>If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court relating to the production of records</li>
        <li>If it was produced by an individual in the course of their employment, business, or profession and the collection is consistent with the purposes for which the information was produced</li>
        <li>If the collection is solely for journalistic, artistic, or literary purposes</li>
        <li>If the information is publicly available and is specified by the regulations</li>
      </ul>
      <p>
        We may disclose de-identified information for approved research or statistics projects,
        subject to ethics oversight and confidentiality commitments.
      </p>

      <h2 id="whoshare">4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
      <p>
        <em>
          In Short: We may share information in specific situations described in this section
          and/or with the following third parties.
        </em>
      </p>
      <p>
        <strong>Vendors, Consultants, and Other Third-Party Service Providers.</strong> We may
        share your data with third-party vendors, service providers, contractors, or agents
        ("third parties") who perform services for us or on our behalf and require access to such
        information to do that work. We have contracts in place with our third parties, which are
        designed to help safeguard your personal information. This means that they cannot do
        anything with your personal information unless we have instructed them to do it. They will
        also not share your personal information with any organization apart from us. They also
        commit to protect the data they hold on our behalf and to retain it for the period we
        instruct.
      </p>
      <p>The third parties we may share personal information with are as follows:</p>
      <ul>
        <li>
          <strong>AI Service Providers</strong> — Anthropic
        </li>
        <li>
          <strong>Cloud Computing Services</strong> — Amazon Web Services (AWS)
        </li>
      </ul>
      <p>We also may need to share your personal information in the following situations:</p>
      <ul>
        <li>
          <strong>Business Transfers.</strong> We may share or transfer your information in
          connection with, or during negotiations of, any merger, sale of company assets,
          financing, or acquisition of all or a portion of our business to another company.
        </li>
      </ul>

      <h2 id="ai">5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</h2>
      <p>
        <em>
          In Short: We offer products, features, or tools powered by artificial intelligence,
          machine learning, or similar technologies.
        </em>
      </p>
      <p>
        As part of our Services, we offer products, features, or tools powered by artificial
        intelligence, machine learning, or similar technologies (collectively, "AI Products").
        These tools are designed to enhance your experience and provide you with innovative
        solutions. The terms in this Privacy Notice govern your use of the AI Products within our
        Services.
      </p>
      <p>
        <strong>Use of AI Technologies</strong>
      </p>
      <p>
        We provide the AI Products through third-party service providers ("AI Service
        Providers"), including Anthropic. The limited task information described in{' '}
        <a href="#infouse">"HOW DO WE PROCESS YOUR INFORMATION?"</a> is shared with and processed
        by these AI Service Providers to enable your use of our AI Products. You must not use the
        AI Products in any way that violates the terms or policies of any AI Service Provider.
      </p>
      <p>
        <strong>Our AI Products</strong>
      </p>
      <p>Our AI Products are designed for the following functions:</p>
      <ul>
        <li>AI-generated productivity reports</li>
      </ul>
      <p>
        <strong>How We Process Your Data Using AI</strong>
      </p>
      <p>
        All personal information processed using our AI Products is handled in line with this
        Privacy Notice and our agreements with our service providers.
      </p>

      <h2 id="intltransfers">6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</h2>
      <p>
        <em>
          In Short: We may transfer, store, and process your information in countries other than
          your own.
        </em>
      </p>
      <p>
        Our servers are located in the United States. Regardless of your location, please be aware
        that your information may be transferred to, stored by, and processed by us in our
        facilities and in the facilities of the third parties with whom we may share your personal
        information (see{' '}
        <a href="#whoshare">"WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"</a> above),
        including facilities in the United States, and other countries.
      </p>
      <p>
        If you are a resident in the European Economic Area (EEA), United Kingdom (UK), or
        Switzerland, then these countries may not necessarily have data protection laws or other
        similar laws as comprehensive as those in your country. However, we will take all
        necessary measures to protect your personal information in accordance with this Privacy
        Notice and applicable law.
      </p>
      <p>
        <strong>European Commission's Standard Contractual Clauses:</strong> We have implemented
        measures to protect your personal information, including by using the European
        Commission's Standard Contractual Clauses for transfers of personal information between
        our group companies and between us and our third-party providers. These clauses require
        all recipients to protect all personal information that they process originating from the
        EEA or UK in accordance with European data protection laws and regulations. Our Standard
        Contractual Clauses can be provided upon request. We have implemented similar appropriate
        safeguards with our third-party service providers and partners and further details can be
        provided upon request.
      </p>

      <h2 id="inforetain">7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
      <p>
        <em>
          In Short: We keep your information for as long as necessary to fulfill the purposes
          outlined in this Privacy Notice unless otherwise required by law.
        </em>
      </p>
      <p>
        We will only keep your personal information for as long as it is necessary for the
        purposes set out in this Privacy Notice, unless a longer retention period is required or
        permitted by law (such as tax, accounting, or other legal requirements). No purpose in
        this notice will require us keeping your personal information for longer than the period
        of time in which users have an account with us.
      </p>
      <p>
        When we have no ongoing legitimate business need to process your personal information, we
        will either delete or anonymize such information, or, if this is not possible (for
        example, because your personal information has been stored in backup archives), then we
        will securely store your personal information and isolate it from any further processing
        until deletion is possible.
      </p>

      <h2 id="infosafe">8. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
      <p>
        <em>
          In Short: We aim to protect your personal information through a system of organizational
          and technical security measures.
        </em>
      </p>
      <p>
        We have implemented appropriate and reasonable technical and organizational security
        measures designed to protect the security of any personal information we process. However,
        despite our safeguards and efforts to secure your information, no electronic transmission
        over the Internet or information storage technology can be guaranteed to be 100% secure,
        so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third
        parties will not be able to defeat our security and improperly collect, access, steal, or
        modify your information. Although we will do our best to protect your personal
        information, transmission of personal information to and from our Services is at your own
        risk. You should only access the Services within a secure environment.
      </p>

      <h2 id="infominors">9. DO WE COLLECT INFORMATION FROM MINORS?</h2>
      <p>
        <em>
          In Short: We do not knowingly collect data from or market to children under 18 years of
          age or the equivalent age as specified by law in your jurisdiction.
        </em>
      </p>
      <p>
        We do not knowingly collect, solicit data from, or market to children under 18 years of
        age or the equivalent age as specified by law in your jurisdiction, nor do we knowingly
        sell such personal information. By using the Services, you represent that you are at least
        18 or the equivalent age as specified by law in your jurisdiction or that you are the
        parent or guardian of such a minor and consent to such minor dependent's use of the
        Services. If we learn that personal information from users less than 18 years of age or
        the equivalent age as specified by law in your jurisdiction has been collected, we will
        deactivate the account and take reasonable measures to promptly delete such data from our
        records. If you become aware of any data we may have collected from children under age 18
        or the equivalent age as specified by law in your jurisdiction, please contact us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a>.
      </p>

      <h2 id="privacyrights">10. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
      <p>
        <em>
          In Short: Depending on your state of residence in the US or in some regions, such as the
          European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have
          rights that allow you greater access to and control over your personal information. You
          may review, change, or terminate your account, depending on your country, province, or
          state of residence.
        </em>
      </p>
      <p>
        In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under
        applicable data protection laws. These may include the right (i) to request access and
        obtain a copy of your personal information, (ii) to request rectification or erasure;
        (iii) to restrict the processing of your personal information; (iv) if applicable, to data
        portability; and (v) not to be subject to automated decision-making. If a decision that
        produces legal or similarly significant effects is made solely by automated means, we will
        inform you, explain the main factors, and offer a simple way to request human review. In
        certain circumstances, you may also have the right to object to the processing of your
        personal information. You can make such a request by contacting us by using the contact
        details provided in the section{' '}
        <a href="#contact">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</a> below. We will consider
        and act upon any request in accordance with applicable data protection laws.
      </p>
      <p>
        If you are located in the UK and are unhappy with how we have handled your personal
        information, you can make a complaint directly to us. This is in addition to the rights
        you have under the UK General Data Protection Regulation and the Data Protection Act 2018.
      </p>
      <ul>
        <li>How to contact us: Email: <a href="mailto:support@ferngate.net">support@ferngate.net</a></li>
        <li>Post: See <a href="#contact">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</a></li>
      </ul>
      <p>
        <strong>What happens after you complain</strong>
      </p>
      <ul>
        <li>We will acknowledge your complaint within 30 days of receiving it.</li>
        <li>We will investigate without unjustifiable or excessive delay.</li>
        <li>We will keep you informed of progress and explain the outcome.</li>
      </ul>
      <p>
        If you are not happy with our final response, you can refer your complaint to the
        Information Commissioner's Office, the UK supervisory authority.
      </p>
      <ul>
        <li>
          Website:{' '}
          <a href="https://ico.org.uk/make-a-complaint" target="_blank" rel="noopener noreferrer">
            ico.org.uk/make-a-complaint
          </a>
        </li>
        <li>Helpline: 0303 123 1113</li>
        <li>
          Post: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire,
          SK9 5AF
        </li>
      </ul>
      <p>
        If you are located in the EEA or UK and you believe we are unlawfully processing your
        personal information, you also have the right to complain to your Member State data
        protection authority or UK data protection authority. If you are located in Switzerland,
        you may contact the Federal Data Protection and Information Commissioner.
      </p>
      <p id="withdrawconsent">
        <strong>Withdrawing your consent:</strong> If we are relying on your consent to process
        your personal information, which may be express and/or implied consent depending on the
        applicable law, you have the right to withdraw your consent at any time. You can withdraw
        your consent at any time by contacting us by using the contact details provided in the
        section <a href="#contact">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</a> below. However,
        please note that this will not affect the lawfulness of the processing before its
        withdrawal nor, when applicable law allows, will it affect the processing of your personal
        information conducted in reliance on lawful processing grounds other than consent.
      </p>
      <p>
        <strong>Account Information</strong>
      </p>
      <p>
        If you would at any time like to review or change the information in your account or
        terminate your account, you can contact us using the contact details provided below. Upon
        your request to terminate your account, we will deactivate or delete your account and
        information from our active databases. However, we may retain some information in our
        files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our
        legal terms and/or comply with applicable legal requirements.
      </p>
      <p>
        If you have questions or comments about your privacy rights, you may email us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a>.
      </p>

      <h2 id="DNT">11. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
      <p>
        Most web browsers and some mobile operating systems and mobile applications include a
        Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference
        not to have data about your online browsing activities monitored and collected. At this
        stage, no uniform technology standard for recognizing and implementing DNT signals has
        been finalized. As such, we do not currently respond to DNT browser signals or any other
        mechanism that automatically communicates your choice not to be tracked online. If a
        standard for online tracking is adopted that we must follow in the future, we will inform
        you about that practice in a revised version of this Privacy Notice.
      </p>
      <p>
        California law requires us to let you know how we respond to web browser DNT signals.
        Because there currently is not an industry or legal standard for recognizing or honoring
        DNT signals, we do not respond to them at this time.
      </p>

      <h2 id="uslaws">12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
      <p>
        <em>
          In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida,
          Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New
          Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right
          to request access to and receive details about the personal information we maintain
          about you and how we have processed it, correct inaccuracies, get a copy of, or delete
          your personal information. You may also have the right to withdraw your consent to our
          processing of your personal information. These rights may be limited in some
          circumstances by applicable law. More information is provided below.
        </em>
      </p>
      <p>
        <strong>Categories of Personal Information We Collect</strong>
      </p>
      <p>
        The table below shows the categories of personal information we have collected in the
        past twelve (12) months. The table includes illustrative examples of each category and
        does not reflect the personal information we collect from you. For a comprehensive
        inventory of all personal information we process, please refer to the section{' '}
        <a href="#infocollect">"WHAT INFORMATION DO WE COLLECT?"</a>
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Examples</th>
              <th>Collected</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>A. Identifiers</td>
              <td>
                Contact details, such as real name, alias, postal address, telephone or mobile
                contact number, unique personal identifier, online identifier, Internet Protocol
                address, email address, and account name
              </td>
              <td>YES</td>
            </tr>
            <tr>
              <td>B. Personal information as defined in the California Customer Records statute</td>
              <td>
                Name, contact information, education, employment, employment history, and
                financial information
              </td>
              <td>YES</td>
            </tr>
            <tr>
              <td>C. Protected classification characteristics under state or federal law</td>
              <td>
                Gender, age, date of birth, race and ethnicity, national origin, marital status,
                and other demographic data
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>D. Commercial information</td>
              <td>
                Transaction information, purchase history, financial details, and payment
                information
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>E. Biometric information</td>
              <td>Fingerprints and voiceprints</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>F. Internet or other similar network activity</td>
              <td>
                Browsing history, search history, online behavior, interest data, and interactions
                with our and other websites, applications, systems, and advertisements
              </td>
              <td>YES</td>
            </tr>
            <tr>
              <td>G. Geolocation data</td>
              <td>Device location</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>H. Audio, electronic, sensory, or similar information</td>
              <td>
                Images and audio, video or call recordings created in connection with our business
                activities
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>I. Professional or employment-related information</td>
              <td>
                Business contact details in order to provide you our Services at a business level
                or job title, work history, and professional qualifications if you apply for a job
                with us
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>J. Education Information</td>
              <td>Student records and directory information</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>K. Inferences drawn from collected personal information</td>
              <td>
                Inferences drawn from any of the collected personal information listed above to
                create a profile or summary about, for example, an individual's preferences and
                characteristics
              </td>
              <td>NO</td>
            </tr>
            <tr>
              <td>L. Sensitive personal Information</td>
              <td>Account login information</td>
              <td>YES</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        We only collect sensitive personal information, as defined by applicable privacy laws or
        the purposes allowed by law or with your consent. Sensitive personal information may be
        used, or disclosed to a service provider or contractor, for additional, specified
        purposes. You may have the right to limit the use or disclosure of your sensitive personal
        information. We do not collect or process sensitive personal information for the purpose
        of inferring characteristics about you.
      </p>
      <p>
        We may also collect other personal information outside of these categories through
        instances where you interact with us in person, online, or by phone or mail in the context
        of:
      </p>
      <ul>
        <li>Receiving help through our customer support channels;</li>
        <li>Participation in customer surveys or contests; and</li>
        <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
      </ul>
      <p>
        We will use and retain the collected personal information as needed to provide the
        Services or for:
      </p>
      <ul>
        <li>Category A - As long as the user has an account with us</li>
        <li>Category B - As long as the user has an account with us</li>
        <li>Category F - As long as the user has an account with us</li>
        <li>Category L - As long as the user has an account with us</li>
      </ul>
      <p>
        <strong>Sources of Personal Information</strong> — Learn more about the sources of
        personal information we collect in{' '}
        <a href="#infocollect">"WHAT INFORMATION DO WE COLLECT?"</a>
      </p>
      <p>
        <strong>How We Use and Share Personal Information</strong> — Learn more about how we use
        your personal information in the section,{' '}
        <a href="#infouse">"HOW DO WE PROCESS YOUR INFORMATION?"</a>
      </p>
      <p>
        <strong>Will your information be shared with anyone else?</strong> We may disclose your
        personal information with our service providers pursuant to a written contract between us
        and each service provider. Learn more about how we disclose personal information in the
        section, <a href="#whoshare">"WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"</a>
      </p>
      <p>
        We may use your personal information for our own business purposes, such as for
        undertaking internal research for technological development and demonstration. This is
        not considered to be "selling" of your personal information.
      </p>
      <p>
        We have not sold or shared any personal information to third parties for a business or
        commercial purpose in the preceding twelve (12) months. We have disclosed the following
        categories of personal information to third parties for a business or commercial purpose
        in the preceding twelve (12) months:
      </p>
      <ul>
        <li>Category A. Identifiers</li>
        <li>Category B. Personal information as defined in the California Customer Records law</li>
        <li>Category F. Internet or other electronic network activity information</li>
        <li>Category L. Sensitive personal information</li>
      </ul>
      <p>
        The categories of third parties to whom we disclosed personal information for a business
        or commercial purpose can be found under{' '}
        <a href="#whoshare">"WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"</a>
      </p>
      <p>
        <strong>Your Rights</strong>
      </p>
      <p>
        You have rights under certain US state data protection laws. However, these rights are not
        absolute, and in certain cases, we may decline your request as permitted by law. These
        rights include:
      </p>
      <ul>
        <li>Right to know whether or not we are processing your personal data</li>
        <li>Right to access your personal data</li>
        <li>Right to correct inaccuracies in your personal data</li>
        <li>Right to request the deletion of your personal data</li>
        <li>Right to obtain a copy of the personal data you previously shared with us</li>
        <li>Right to non-discrimination for exercising your rights</li>
        <li>
          Right to opt out of the processing of your personal data if it is used for targeted
          advertising (or sharing as defined under California's privacy law), the sale of personal
          data, or profiling in furtherance of decisions that produce legal or similarly
          significant effects ("profiling")
        </li>
      </ul>
      <p>Depending upon the state where you live, you may also have the following rights:</p>
      <ul>
        <li>
          Right to access the categories of personal data being processed (as permitted by
          applicable law, including the privacy law in Minnesota)
        </li>
        <li>
          Right to obtain a list of the categories of third parties to which we have disclosed
          personal data (as permitted by applicable law, including the privacy law in California,
          Delaware, and Maryland)
        </li>
        <li>
          Right to obtain a list of specific third parties to which we have disclosed personal
          data (as permitted by applicable law, including the privacy law in Minnesota and Oregon)
        </li>
        <li>
          Right to obtain a list of third parties to which we have sold personal data (as
          permitted by applicable law, including the privacy law in Connecticut)
        </li>
        <li>
          Right to review, understand, question, and depending on where you live, correct how
          personal data has been profiled (as permitted by applicable law, including the privacy
          law in Connecticut and Minnesota)
        </li>
        <li>
          Right to limit use and disclosure of sensitive personal data (as permitted by applicable
          law, including the privacy law in California)
        </li>
        <li>
          Right to opt out of the collection of sensitive data and personal data collected through
          the operation of a voice or facial recognition feature (as permitted by applicable law,
          including the privacy law in Florida)
        </li>
      </ul>
      <p>
        <strong>How to Exercise Your Rights</strong>
      </p>
      <p>
        To exercise these rights, you can contact us by emailing us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a>, or by referring to the
        contact details at the bottom of this document. Under certain US state data protection
        laws, you can designate an authorized agent to make a request on your behalf. We may deny
        a request from an authorized agent that does not submit proof that they have been validly
        authorized to act on your behalf in accordance with applicable laws.
      </p>
      <p>
        <strong>Request Verification</strong>
      </p>
      <p>
        Upon receiving your request, we will need to verify your identity to determine you are the
        same person about whom we have the information in our system. We will only use personal
        information provided in your request to verify your identity or authority to make the
        request. However, if we cannot verify your identity from the information already
        maintained by us, we may request that you provide additional information for the purposes
        of verifying your identity and for security or fraud-prevention purposes. If you submit
        the request through an authorized agent, we may need to collect additional information to
        verify your identity before processing your request and the agent will need to provide a
        written and signed permission from you to submit such request on your behalf.
      </p>
      <p>
        <strong>Appeals</strong>
      </p>
      <p>
        Under certain US state data protection laws, if we decline to take action regarding your
        request, you may appeal our decision by emailing us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a>. We will inform you in
        writing of any action taken or not taken in response to the appeal, including a written
        explanation of the reasons for the decisions. If your appeal is denied, you may submit a
        complaint to your state attorney general.
      </p>
      <p>
        <strong>California "Shine The Light" Law</strong>
      </p>
      <p>
        California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits
        our users who are California residents to request and obtain from us, once a year and free
        of charge, information about categories of personal information (if any) we disclosed to
        third parties for direct marketing purposes and the names and addresses of all third
        parties with which we shared personal information in the immediately preceding calendar
        year. If you are a California resident and would like to make such a request, please
        submit your request in writing to us by using the contact details provided in the section{' '}
        <a href="#contact">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</a>
      </p>

      <h2 id="clausea">13. A NOTE ON WHAT YOU ENTER</h2>
      <p>
        Because task titles are processed by an AI system to generate your reports, we recommend
        avoiding highly sensitive information (such as full account numbers, passwords, or
        detailed medical information) in task titles.
      </p>

      <h2 id="policyupdates">14. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
      <p>
        <em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em>
      </p>
      <p>
        We may update this Privacy Notice from time to time. The updated version will be indicated
        by an updated "Revised" date at the top of this Privacy Notice. If we make material
        changes to this Privacy Notice, we may notify you either by prominently posting a notice
        of such changes or by directly sending you a notification. We encourage you to review this
        Privacy Notice frequently to be informed of how we are protecting your information.
      </p>

      <h2 id="contact">15. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
      <p>
        If you have questions or comments about this notice, you may email us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a> or contact us by post at:
      </p>
      <p>
        Ferngate LLC
        <br />
        116 AGNES RD STE 200
        <br />
        Knoxville, TN 37919
        <br />
        United States
      </p>

      <h2 id="request">
        16. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
      </h2>
      <p>
        Based on the applicable laws of your country or state of residence in the US, you may have
        the right to request access to the personal information we collect from you, details about
        how we have processed it, correct inaccuracies, or delete your personal information. You
        may also have the right to withdraw your consent to our processing of your personal
        information. These rights may be limited in some circumstances by applicable law. To
        request to review, update, or delete your personal information, please contact us at{' '}
        <a href="mailto:support@ferngate.net">support@ferngate.net</a>.
      </p>

      <p className="legal-page__credit">This Privacy Policy was created using Termly's Privacy Policy Generator.</p>
    </>
  );
}
